/**
 * Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Abfrage bei Concierge":
 * Universeller PIN-Prompt für Team-User (admin + concierge). Analog zum
 * `CustomerPinPromptDialog`, aber für die persönliche Team-PIN, die
 * Buchungsänderungen absichert.
 *
 * Modes:
 *   - "confirm" · PIN eingeben (4 Ziffern), onConfirm(pin) wird ausgelöst
 *   - "setup"   · Erst-Einrichtung: neue PIN + Bestätigung (POST /me/team-pin)
 *   - "change"  · Alte PIN + neue PIN + Bestätigung (PUT /me/team-pin)
 *   - "reset"   · E-Mail-OTP + neue PIN (nach „PIN vergessen")
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, ShieldCheck } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";

const PIN_LEN = 4;

function PinInput({ value, onChange, autoFocus = false, disabled = false, testIdPrefix = "team-pin" }) {
  const refs = useRef([]);
  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);
  const setDigit = (idx, ch) => {
    const clean = (ch || "").replace(/\D/g, "").slice(0, 1);
    const arr = value.split("");
    while (arr.length < PIN_LEN) arr.push("");
    arr[idx] = clean;
    onChange(arr.join("").slice(0, PIN_LEN));
    if (clean && idx < PIN_LEN - 1) refs.current[idx + 1]?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !value[idx] && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < PIN_LEN - 1) refs.current[idx + 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, PIN_LEN);
    if (!txt) return;
    e.preventDefault();
    onChange(txt.padEnd(PIN_LEN, "").slice(0, PIN_LEN));
    const nextIdx = Math.min(txt.length, PIN_LEN - 1);
    refs.current[nextIdx]?.focus();
  };
  return (
    <div className="flex gap-2.5 justify-center" onPaste={onPaste}>
      {Array.from({ length: PIN_LEN }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          data-testid={`${testIdPrefix}-digit-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          disabled={disabled}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          className="w-12 h-14 border border-navy/20 focus:border-gold focus:outline-none text-center font-display text-2xl text-navy bg-white disabled:opacity-50"
        />
      ))}
    </div>
  );
}

export default function TeamPinPromptDialog({
  open,
  mode = "confirm",
  title,
  description,
  submitLabel,
  onClose,
  onConfirm,
  onSuccess,
}) {
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestingReset, setRequestingReset] = useState(false);

  useEffect(() => {
    if (open) {
      setPin("");
      setNewPin("");
      setConfirmPin("");
      setResetOtp("");
      setBusy(false);
    }
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape" && !busy) onClose?.(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, busy, onClose]);

  if (!open) return null;

  const requestReset = async () => {
    setRequestingReset(true);
    try {
      await api.post("/me/team-pin/request-reset");
      toast.success("Reset-Code an Ihre E-Mail gesendet. Prüfen Sie Ihr Postfach.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setRequestingReset(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;

    if (mode === "confirm") {
      if (pin.length !== PIN_LEN) {
        toast.error("Bitte 4-stellige PIN eingeben.");
        return;
      }
      setBusy(true);
      try {
        await onConfirm?.(pin);
      } catch (err) {
        const msg = formatErr(err?.response?.data?.detail) || err?.message;
        if (msg) toast.error(msg);
      } finally {
        setBusy(false);
      }
      return;
    }

    if (mode === "setup") {
      if (newPin.length !== PIN_LEN) {
        toast.error("Neue PIN muss 4 Ziffern haben.");
        return;
      }
      if (newPin !== confirmPin) {
        toast.error("PINs stimmen nicht überein.");
        return;
      }
      setBusy(true);
      try {
        await api.post("/me/team-pin", { new_pin: newPin });
        toast.success("PIN gespeichert.");
        onSuccess?.(newPin);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }

    if (mode === "change") {
      if (pin.length !== PIN_LEN) {
        toast.error("Bitte aktuelle PIN eingeben.");
        return;
      }
      if (newPin.length !== PIN_LEN || newPin !== confirmPin) {
        toast.error("Neue PIN prüfen (4 Ziffern, muss übereinstimmen).");
        return;
      }
      setBusy(true);
      try {
        await api.put("/me/team-pin", { current_pin: pin, new_pin: newPin });
        toast.success("PIN aktualisiert.");
        onSuccess?.(newPin);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }

    if (mode === "reset") {
      if (!resetOtp.trim()) {
        toast.error("Bitte Reset-Code eingeben.");
        return;
      }
      if (newPin.length !== PIN_LEN || newPin !== confirmPin) {
        toast.error("Neue PIN prüfen (4 Ziffern, muss übereinstimmen).");
        return;
      }
      setBusy(true);
      try {
        await api.put("/me/team-pin", { reset_otp: resetOtp.trim(), new_pin: newPin });
        toast.success("PIN neu vergeben.");
        onSuccess?.(newPin);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }
  };

  const heading =
    title ||
    (mode === "confirm" ? "PIN bestätigen"
      : mode === "setup" ? "Persönliche PIN einrichten"
      : mode === "change" ? "PIN ändern"
      : "PIN zurücksetzen");

  const helper =
    description ||
    (mode === "confirm"
      ? "Bitte geben Sie Ihre persönliche 4-stellige Team-PIN ein, um die Änderung zu bestätigen."
      : mode === "setup"
      ? "Vergeben Sie eine 4-stellige PIN. Sie dient künftig als digitale Signatur für jede Änderung an Buchungen, die Sie speichern."
      : mode === "change"
      ? "Bitte aktuelle PIN und dann die neue PIN eingeben."
      : "Geben Sie den Reset-Code aus Ihrer E-Mail und eine neue PIN ein.");

  const submitText =
    submitLabel ||
    (mode === "confirm" ? "Bestätigen"
      : mode === "setup" ? "PIN speichern"
      : mode === "change" ? "PIN ändern"
      : "PIN zurücksetzen");

  return createPortal(
    <div
      data-testid="team-pin-dialog"
      data-mode={mode}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-navy/60 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <form
        onSubmit={submit}
        className="bg-white border border-navy/15 w-full max-w-md p-7 md:p-8 relative"
        data-testid="team-pin-dialog-form"
      >
        <button
          type="button"
          data-testid="team-pin-dialog-close"
          onClick={() => !busy && onClose?.()}
          className="absolute top-4 right-4 text-navy-dark/50 hover:text-navy transition-colors"
          aria-label="Schließen"
        >
          <X className="w-5 h-5" strokeWidth={1.6} />
        </button>

        <div className="flex items-center gap-2.5 mb-2">
          <ShieldCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
          <div className="label-caps">Persönliche Team-PIN</div>
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-navy leading-tight" data-testid="team-pin-dialog-title">
          {heading}
        </h3>
        <p className="text-[13px] text-navy-dark/70 leading-relaxed mt-2 mb-6">
          {helper}
        </p>

        {mode === "confirm" && (
          <div className="space-y-2 mb-2" data-testid="team-pin-confirm-section">
            <div className="label-caps text-center">Ihre PIN</div>
            <PinInput value={pin} onChange={setPin} autoFocus disabled={busy} testIdPrefix="team-pin-confirm" />
            <div className="text-center pt-1">
              <button
                type="button"
                data-testid="team-pin-forgot-link"
                onClick={requestReset}
                disabled={busy || requestingReset}
                className="text-[11.5px] tracking-[0.12em] uppercase text-navy-dark/60 hover:text-gold transition-colors disabled:opacity-50"
              >
                {requestingReset ? "Code wird gesendet…" : "PIN vergessen? Code per E-Mail senden"}
              </button>
            </div>
          </div>
        )}

        {mode === "setup" && (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="label-caps text-center">Neue PIN</div>
              <PinInput value={newPin} onChange={setNewPin} autoFocus disabled={busy} testIdPrefix="team-pin-new" />
            </div>
            <div className="space-y-2">
              <div className="label-caps text-center">PIN wiederholen</div>
              <PinInput value={confirmPin} onChange={setConfirmPin} disabled={busy} testIdPrefix="team-pin-confirm-new" />
            </div>
          </div>
        )}

        {mode === "change" && (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="label-caps text-center">Aktuelle PIN</div>
              <PinInput value={pin} onChange={setPin} autoFocus disabled={busy} testIdPrefix="team-pin-current" />
              <div className="text-center">
                <button
                  type="button"
                  data-testid="team-pin-change-forgot-link"
                  onClick={requestReset}
                  disabled={busy || requestingReset}
                  className="text-[11.5px] tracking-[0.12em] uppercase text-navy-dark/60 hover:text-gold transition-colors disabled:opacity-50"
                >
                  {requestingReset ? "Code wird gesendet…" : "PIN vergessen? Code per E-Mail senden"}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="label-caps text-center">Neue PIN</div>
              <PinInput value={newPin} onChange={setNewPin} disabled={busy} testIdPrefix="team-pin-new" />
            </div>
            <div className="space-y-2">
              <div className="label-caps text-center">Neue PIN wiederholen</div>
              <PinInput value={confirmPin} onChange={setConfirmPin} disabled={busy} testIdPrefix="team-pin-confirm-new" />
            </div>
          </div>
        )}

        {mode === "reset" && (
          <div className="space-y-5">
            <div>
              <label className="label-caps block mb-2" htmlFor="team-pin-reset-otp">
                Reset-Code aus E-Mail
              </label>
              <input
                id="team-pin-reset-otp"
                data-testid="team-pin-reset-otp"
                value={resetOtp}
                onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="12345678"
                autoFocus
                disabled={busy}
                className="w-full border border-navy/20 px-4 py-3 text-[15px] tracking-[0.3em] text-center font-display text-navy focus:border-gold focus:outline-none"
              />
              <p className="text-[11.5px] text-navy-dark/55 mt-2 italic text-center">
                Der 8-stellige Code aus der E-Mail. 10 Minuten gültig.
              </p>
            </div>
            <div className="space-y-2">
              <div className="label-caps text-center">Neue PIN</div>
              <PinInput value={newPin} onChange={setNewPin} disabled={busy} testIdPrefix="team-pin-new" />
            </div>
            <div className="space-y-2">
              <div className="label-caps text-center">Neue PIN wiederholen</div>
              <PinInput value={confirmPin} onChange={setConfirmPin} disabled={busy} testIdPrefix="team-pin-confirm-new" />
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse md:flex-row gap-3 mt-7">
          <button
            type="button"
            data-testid="team-pin-dialog-cancel"
            onClick={() => !busy && onClose?.()}
            disabled={busy}
            className="flex-1 border border-navy/25 text-navy px-5 py-3 text-[11.5px] tracking-[0.18em] uppercase hover:bg-navy/5 transition-colors disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            data-testid="team-pin-dialog-submit"
            disabled={busy}
            className="flex-1 text-ivory px-5 py-3 text-[11.5px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}
          >
            {busy ? "Wird geprüft…" : submitText}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
